import { useEffect, useRef, useState } from "react";
import { sendChatMessage } from "../chatbotApi";
import { submitLead, confirmPayment } from "./widgetApi";
import { FLOW_MAP, AI_WELCOME_TEXT, buildSummaryRows, buildLeadPayload } from "./flowConfig";
import { getFbc, getMetaCookie, trackPixelEvent, sleep, isChatbotPaymentReturn } from "./utils";

const STORE_KEY = "tgh_widget_v2";
const SESSION_KEY = "tgh_chat_session"; // shared with the /chatbot page

let counter = 0;
const uid = () => `${Date.now()}-${counter++}`;

function loadSaved() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Owns all state for the floating widget:
 *  - scripted "Submit Enquiry" flow
 *  - AI chat mode (same backend + session as the /chatbot page)
 *  - payment-return handling (?bookingId=...)
 */
export default function useWidgetChat() {
  const [saved] = useState(loadSaved);

  const [messages, setMessages] = useState(saved?.messages || []);
  const [stepId, setStepId] = useState(saved?.stepId || null);
  const [answers, setAnswers] = useState(saved?.answers || {});
  const [aiMode, setAiMode] = useState(!!saved?.aiMode);
  const [completed, setCompleted] = useState(!!saved?.completed);
  const [typing, setTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const answersRef = useRef(answers);
  const messagesRef = useRef(messages);
  const epochRef = useRef(0); // bumps on reset so stale async chains stop
  const startingRef = useRef(false);
  const paymentHandledRef = useRef(false);
  messagesRef.current = messages;

  const busy = typing || loading;

  // ---- persistence ----
  useEffect(() => {
    try {
      localStorage.setItem(
        STORE_KEY,
        JSON.stringify({ messages: messages.slice(-80), stepId, answers, aiMode, completed })
      );
    } catch (e) {
      /* storage full / blocked — ignore */
    }
  }, [messages, stepId, answers, aiMode, completed]);

  // ---- message helpers ----
  const pushMsg = (m) => setMessages((prev) => [...prev, { id: uid(), time: Date.now(), ...m }]);

  // Shows the typing bubble, then the message. Returns false if the chat was reset meanwhile.
  async function botSay(text, extra) {
    const epoch = epochRef.current;
    setTyping(true);
    await sleep(600);
    if (epoch !== epochRef.current) return false;
    setTyping(false);
    pushMsg({ role: "bot", kind: "text", text, ...extra });
    return true;
  }

  // ---- scripted flow ----
  async function goToStep(id) {
    const step = FLOW_MAP[id];
    if (!step) return console.error("Step not found:", id);
    setStepId(null);

    if (step.type === "ai") {
      setAiMode(true);
      await botSay(AI_WELCOME_TEXT, { kind: "ai" });
      return;
    }
    if (step.type === "summary") {
      await finishEnquiry();
      return;
    }
    if (await botSay(step.bot)) setStepId(id);
  }

  async function finishEnquiry() {
    const a = answersRef.current;
    pushMsg({ role: "bot", kind: "summary", rows: buildSummaryRows(a) });
    setCompleted(true);

    submitLead(buildLeadPayload(a, getMetaCookie("_fbp"), getFbc()))
      .then((result) =>
        trackPixelEvent(
          "Lead",
          {
            content_name: `chatbot_${(a.helpType || "general").toLowerCase()}_lead`,
            content_category: a.helpType || "general",
            value: 0,
            currency: "INR",
          },
          result?.eventId
        )
      )
      .catch((err) => console.error("Lead error:", err));

    if (!(await botSay("Thank you! Our team will contact you shortly."))) return;
    await sleep(400);
    if (await botSay(FLOW_MAP.again.bot)) setStepId("again");
  }

  async function submitAnswer(value) {
    const step = FLOW_MAP[stepId];
    const text = String(value ?? "").trim();
    if (!step || busy || !text) return;

    pushMsg({ role: "user", kind: "text", text });

    if (step.id !== "again") {
      const next = { ...answersRef.current, [step.id]: text };
      answersRef.current = next;
      setAnswers(next);
    }

    const next = step.nextMap
      ? step.nextMap[text]
      : typeof step.next === "function"
      ? step.next(text)
      : step.next;

    setStepId(null);

    if (next === "__restart") {
      if (await botSay("Great! Let's start a new enquiry")) resetChat();
      return;
    }
    if (next === "__end") {
      await botSay("Thanks for chatting with us! Have a great day");
      return;
    }
    if (!next) return console.error("No next step:", step.id, text);
    await goToStep(next);
  }

  // Called when the widget is opened for the first time
  function ensureStarted() {
    if (messagesRef.current.length > 0 || startingRef.current || paymentProcessing) return;
    startingRef.current = true;
    goToStep("entryMode").finally(() => {
      startingRef.current = false;
    });
  }

  function resetChat() {
    epochRef.current += 1;
    answersRef.current = {};
    messagesRef.current = [];
    setAnswers({});
    setMessages([]);
    setStepId(null);
    setAiMode(false);
    setCompleted(false);
    setTyping(false);
    setLoading(false);
    sessionStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(STORE_KEY);
    startingRef.current = true;
    goToStep("entryMode").finally(() => {
      startingRef.current = false;
    });
  }

  // ---- AI chat ----
  async function sendAi(raw) {
    const text = String(raw ?? "").trim().slice(0, 1000);
    if (!text || busy) return;

    pushMsg({ role: "user", kind: "text", text });
    setLoading(true);
    const epoch = epochRef.current;

    try {
      // read fresh: the /chatbot page may have started/cleared the session
      const sessionId = sessionStorage.getItem(SESSION_KEY) || undefined;
      const data = await sendChatMessage({ message: text, sessionId });
      if (epoch !== epochRef.current) return;

      if (data.sessionId) sessionStorage.setItem(SESSION_KEY, data.sessionId);

      pushMsg({
        role: "bot",
        kind: "ai",
        stamp: true,
        text: data.response || "Happy to help",
        toolsUsed: data.toolsUsed || [],
        preview: data.preview || null,
        cards: data.cards || null,
      });
    } catch (err) {
      console.error("[FloatingChatbot] AI error:", err);
      if (epoch === epochRef.current) {
        pushMsg({
          role: "bot",
          kind: "text",
          text: "Sorry, I encountered an issue retrieving data. Please try again or reach our team.",
        });
      }
    } finally {
      if (epoch === epochRef.current) setLoading(false);
    }
  }

  // ---- payment redirect (?bookingId=...&razorpay_payment_id=...) ----
  useEffect(() => {
    if (paymentHandledRef.current) return;
    // only react to payments started from the chatbot (?src=chatbot) - not the normal booking flow
    if (!isChatbotPaymentReturn()) return;
    const params = new URLSearchParams(window.location.search);
    const bookingId = params.get("bookingId");
    paymentHandledRef.current = true;

    const paymentId = params.get("razorpay_payment_id");
    setPaymentProcessing(true);
    setStepId(null);
    setAiMode(false);
    setMessages([
      { id: uid(), time: Date.now(), role: "bot", kind: "text", text: "Processing your payment... Please wait ⏳" },
    ]);

    (async () => {
      try {
        const data = await confirmPayment({ bookingMongoId: bookingId, razorpay_payment_id: paymentId });
        setMessages([
          { id: uid(), time: Date.now(), role: "bot", kind: "invoice", url: data?.invoiceLink || "#" },
        ]);
        setAiMode(true);
      } catch (err) {
        console.error("Invoice/payment error:", err);
        setMessages([{ id: uid(), time: Date.now(), role: "bot", kind: "text", text: "Invoice generation failed." }]);
      } finally {
        setPaymentProcessing(false);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    })();
  }, []);

  return {
    messages, stepId, answers, aiMode, completed,
    typing, loading, busy, paymentProcessing,
    hasOngoingChat: messages.length > 0 && !completed,
    ensureStarted, submitAnswer, sendAi, resetChat,
  };
}